import { ClerkLoaded, ClerkLoading, useAuth } from '@clerk/react';
import { Box, Container, Skeleton, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { type ReactNode, useRef } from 'react';
import { Navigate } from 'react-router';
import { colors } from '../../theme/ai-theme';
import { auroraDrift, fadeSlideUp } from '../../theme/shared-styles';

interface AuthLayoutProps {
  children: ReactNode;
  banner?: ReactNode;
}

export function AuthLayout({ children, banner }: AuthLayoutProps) {
  const { isSignedIn, isLoaded } = useAuth();
  const streakRef = useRef<HTMLSpanElement>(null);
  const triggerShine = () => {
    const el = streakRef.current;
    if (!el) {
      return;
    }
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }
    el.animate([{ backgroundPosition: '100% 0' }, { backgroundPosition: '0% 0' }], {
      duration: 700,
      easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
    });
  };

  if (isLoaded && isSignedIn) {
    return <Navigate to="/" replace />;
  }

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        bgcolor: colors.background.default,
        // Aurora lives on a ::before larger than the viewport so the slow drift
        // never reveals an edge. transform is GPU-composited (smooth on mobile).
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: '-20%',
          zIndex: 0,
          pointerEvents: 'none',
          background: `
            radial-gradient(ellipse at 20% 20%, ${alpha(colors.primary.main, 0.07)} 0%, transparent 50%),
            radial-gradient(ellipse at 80% 80%, ${alpha(colors.secondary.main, 0.05)} 0%, transparent 50%),
            radial-gradient(ellipse at 50% 0%, ${alpha(colors.primary.main, 0.1)} 0%, transparent 60%)
          `,
          animation: `${auroraDrift} 30s ease-in-out infinite alternate`,
          willChange: 'transform',
        },
        '& > *': { position: 'relative', zIndex: 1 },
        '@media (prefers-reduced-motion: reduce)': {
          '&::before': { animation: 'none' },
        },
      }}
    >
      <Container maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3,
            animation: `${fadeSlideUp} 0.5s ease-out`,
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 1 }}>
            <Box
              component="span"
              onClick={triggerShine}
              sx={{
                display: 'inline-block',
                position: 'relative',
                cursor: 'pointer',
                lineHeight: 1,
                fontWeight: 700,
                letterSpacing: '-0.02em',
                fontSize: { xs: '1.85rem', sm: '2.125rem' },
                verticalAlign: 'bottom',
              }}
            >
              <Box
                component="span"
                sx={{
                  background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.primary.light} 50%, ${colors.secondary.main} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Prospero
              </Box>
              <Box
                ref={streakRef}
                component="span"
                aria-hidden
                sx={{
                  position: 'absolute',
                  inset: 0,
                  background:
                    'linear-gradient(110deg, transparent 40%, rgba(255,255,255,0.85) 50%, transparent 60%)',
                  backgroundSize: '300% 100%',
                  backgroundPosition: '100% 0',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  pointerEvents: 'none',
                }}
              >
                Prospero
              </Box>
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                mt: 0.5,
                fontWeight: 400,
              }}
            >
              HACCP Compliance Management
            </Typography>
          </Box>

          {banner}

          <Box sx={{ width: '100%', minHeight: 480, display: 'flex', justifyContent: 'center' }}>
            <ClerkLoading>
              <Box
                sx={{
                  width: '100%',
                  maxWidth: 400,
                  bgcolor: 'background.paper',
                  borderRadius: '16px',
                  p: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                }}
              >
                <Skeleton variant="rounded" height={44} />
                <Skeleton variant="rounded" height={44} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1 }}>
                  <Skeleton variant="rectangular" height={1} sx={{ flex: 1 }} />
                  <Skeleton variant="text" width={24} />
                  <Skeleton variant="rectangular" height={1} sx={{ flex: 1 }} />
                </Box>
                <Skeleton variant="text" width={80} height={20} />
                <Skeleton variant="rounded" height={48} />
                <Skeleton variant="text" width={80} height={20} sx={{ mt: 1 }} />
                <Skeleton variant="rounded" height={48} />
                <Skeleton variant="rounded" height={48} sx={{ mt: 1 }} />
              </Box>
            </ClerkLoading>
            <ClerkLoaded>{children}</ClerkLoaded>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
